/**
 * LearnerHubOverview — the demoted-but-alive 5-card dashboard.
 *
 * Operator brief (May 19, 2026 visual-baseline shift):
 *   "If deeper dashboard content still exists, it should appear
 *    AFTER intent selection/navigation — not dumped onto the
 *    initial screen."
 *
 * This route exists so the Phase 1.3 alive-contract work
 * (LatestReviewCard, UpcomingLessonReminderBanner,
 * LearnerHomeworkList, LearnerFrameworkStrip, etc.) stays alive
 * and reachable by learners who want the broader picture, by
 * support docs that linked to specific cards, and by the
 * intent-first fallback router on the new /learn screen.
 *
 * The previous LearnerHub header chrome lives here so all the
 * "find another route" affordances (notifications bell, manual,
 * recent activity, dark mode toggle, logout) remain available
 * without polluting the calm intent-first home.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, HelpCircle, BookOpen as BookManualIcon, History, ArrowLeft } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { DarkModeToggle } from "../components/DarkModeToggle";
import AppNotificationsBell from "../components/AppNotificationsBell";
import WelcomeTour from "../components/WelcomeTour";
import LearnerBottomNav from "../components/learner/LearnerBottomNav";
import { useSessionKeepAlive } from "../hooks/useSessionKeepAlive";
import PinIndicator from "../components/pins/PinIndicator";
import LearnerStateHome from "../components/learner/LearnerStateHome";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LearnerHubOverview() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [replayTour, setReplayTour] = useState(false);
  const [trackState, setTrackState] = useState(null);

  const loadTrack = async () => {
    try {
      const r = await axios.get(`${API}/api/tracks/me`, { withCredentials: true });
      setTrackState(r.data || {});
    } catch {
      setTrackState({});
    }
  };
  useEffect(() => { if (user) loadTrack(); }, [user]);

  const isIndependent = !!trackState?.is_independent;
  const trackAnswered = !!trackState?.track_explicitly_set;
  const isLearnerViewer =
    trackState?.is_learner !== false &&
    (user?.role || "").toLowerCase() === "learner";
  const showTrackOnboarding =
    trackState !== null &&
    !trackAnswered &&
    !trackState?.anonymous &&
    isLearnerViewer;

  useSessionKeepAlive(!!user, { label: "learner_hub_overview" });

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              data-testid="overview-back-to-home"
              onClick={() => nav("/learn")}
              className="p-2 -ml-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Back to home"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 data-testid="learner-name" className="text-xl font-extrabold text-slate-900 dark:text-white truncate">{user?.name || "Learner"}</h1>
              <p className="text-xs text-slate-400 font-medium">Your overview</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <PinIndicator />
            <AppNotificationsBell />
            <button
              type="button"
              data-testid="welcome-tour-replay"
              title="Replay welcome tour"
              onClick={async () => {
                try {
                  await axios.post(`${API}/api/onboarding/welcome/reset`, {}, { withCredentials: true });
                } catch { /* best-effort */ }
                setReplayTour(true);
              }}
              className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            <button
              type="button"
              data-testid="open-user-manual"
              title="Open the user manual"
              onClick={() => nav("/help/learner")}
              className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <BookManualIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              data-testid="open-recent-activity"
              title="Recent activity"
              onClick={() => nav("/operations")}
              className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <History className="w-5 h-5" />
            </button>
            <DarkModeToggle />
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); nav("/login"); }}
              className="p-2.5 rounded-xl text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 mt-5 pb-24">
        <WelcomeTour forceOpen={replayTour} onClose={() => setReplayTour(false)} />
        <LearnerStateHome
          showTrackOnboarding={showTrackOnboarding}
          isIndependent={isIndependent}
          trackState={trackState}
          onTrackAnswered={() => loadTrack()}
        />
      </div>

      <LearnerBottomNav active="home" />
    </div>
  );
}
